/**
 * UTM and referrer capture for reporting. Read once on land and kept for the
 * page's life, because a guest who navigates within the app loses the query
 * string but their session still belongs to the campaign that sent them.
 */

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  /** Free-form: which QR code / poster / theater. */
  placement?: string;
  referrer?: string;
  landedAt: number;
}

const KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'placement',
] as const;

const STORAGE_KEY = 'incomer.attribution';

let cached: Attribution | null = null;

export function captureAttribution(search: string = window.location.search): Attribution {
  if (cached) return cached;

  const stored = readStored();
  if (stored) {
    cached = stored;
    return stored;
  }

  const params = new URLSearchParams(search);
  const attribution: Attribution = { landedAt: Date.now() };
  for (const key of KEYS) {
    const value = params.get(key);
    if (value) attribution[key] = value.slice(0, 128);
  }
  if (document.referrer) attribution.referrer = document.referrer.slice(0, 256);

  cached = attribution;
  writeStored(attribution);
  return attribution;
}

export function getAttribution(): Attribution {
  return cached ?? captureAttribution();
}

/** sessionStorage can throw in private mode / a locked-down WebView. */
function readStored(): Attribution | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function writeStored(attribution: Attribution): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    /* not worth failing a session over */
  }
}
